import { useMutation, useQueryClient } from '@tanstack/react-query';
import { formatCount } from '@/utils/number';
import {
  publishPcmpUrgentBatch,
  rollbackPcmpUrgentBatch,
  stagePcmpUrgentBatch,
} from '../api/pcmpUrgent.api';
import type {
  PcmpUrgentPublishResult,
  PcmpUrgentRollbackPayload,
  PcmpUrgentRollbackResult,
  PcmpUrgentStagePayload,
  PcmpUrgentStageResult,
} from '../types/pcmpUrgent.types';
import { pcmpActionCallbacks } from '../utils/pcmpActionCallbacks';

export function useStagePcmpUrgentBatch(onSuccess: (result: PcmpUrgentStageResult) => void) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: PcmpUrgentStagePayload) => stagePcmpUrgentBatch(payload),
    ...pcmpActionCallbacks<PcmpUrgentStageResult>(
      queryClient,
      (result) =>
        `${formatCount(result.staged.length, 'proposal')} staged in urgent batch #${result.batchNo}`,
      onSuccess,
    ),
  });
}

export function usePublishPcmpUrgentBatch(
  batchNo: number,
  onSuccess: (result: PcmpUrgentPublishResult) => void,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (comment: string | undefined) => publishPcmpUrgentBatch(batchNo, comment),
    ...pcmpActionCallbacks<PcmpUrgentPublishResult>(
      queryClient,
      `Urgent batch #${batchNo} published`,
      onSuccess,
    ),
  });
}

export function useRollbackPcmpUrgentBatch(
  batchNo: number,
  onSuccess: (result: PcmpUrgentRollbackResult) => void,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: PcmpUrgentRollbackPayload) => rollbackPcmpUrgentBatch(batchNo, payload),
    ...pcmpActionCallbacks<PcmpUrgentRollbackResult>(
      queryClient,
      (result) => `${formatCount(result.rolledBack.length, 'proposal')} rolled back to Pre-Batch`,
      onSuccess,
    ),
  });
}
